/**
 * Weather warning configuration
 * Defines thresholds and severity levels for weather-related warnings
 */

export const WarningTypes = {
  SNOW: 'snow',
  STORM: 'storm',
  HEAVY_RAIN: 'heavy_rain',
  STRONG_WIND: 'strong_wind',
  FOG: 'fog',
  ICE: 'ice',
  EXTREME_HEAT: 'extreme_heat',
  EXTREME_COLD: 'extreme_cold',
};

export const WarningSeverity = {
  INFO: 'info',
  WARNING: 'warning',
  DANGER: 'danger',
  EXTREME: 'extreme',
};

// Weather codes that indicate dangerous conditions
export const dangerousWeatherCodes = {
  // Snow conditions
  snow: [
    // AEMET codes
    '33', '33n', '34', '34n', '35', '35n', '36', '36n',
    '71', '71n', '72', '72n', '73', '73n', '74', '74n',
    // Google codes
    'LIGHT_SNOW', 'SNOW', 'HEAVY_SNOW', 'SNOW_SHOWERS', 'SLEET', 'BLIZZARD',
    'HAIL', 'ICE',
  ],
  // Storm conditions
  storm: [
    // AEMET codes
    '51', '51n', '52', '52n', '53', '53n', '54', '54n',
    '61', '61n', '62', '62n', '63', '63n', '64', '64n',
    // Google codes
    'THUNDERSTORM',
  ],
  // Heavy rain
  heavyRain: [
    // AEMET codes
    '25', '25n', '26', '26n', '27', '27n',
    // Google codes
    'HEAVY_RAIN',
  ],
  // Fog/Mist
  fog: [
    // AEMET codes
    '81', '81n', '82', '82n', '83', '83n',
    // Google codes
    'FOGGY',
  ],
};

// Thresholds for different warning types
export const warningThresholds = {
  wind: {
    info: 50, // km/h
    warning: 70, // km/h
    danger: 90, // km/h
    extreme: 120, // km/h
  },
  windGusts: {
    info: 60,
    warning: 80,
    danger: 110,
    extreme: 150,
  },
  precipitation: {
    info: 5, // mm
    warning: 10, // mm
    danger: 20, // mm
    extreme: 40, // mm
  },
  stormProbability: {
    info: 35, // %
    warning: 60, // %
    danger: 75, // %
    extreme: 90, // %
  },
  snowProbability: {
    info: 25, // %
    warning: 50, // %
    danger: 70, // %
    extreme: 85, // %
  },
  temperature: {
    extremeCold: -5, // °C
    cold: 2, // °C
    hot: 35, // °C
    extremeHot: 40, // °C
  },
};

// Warning messages in Spanish
export const warningMessages = {
  [WarningTypes.SNOW]: {
    title: 'Alerta por Nieve',
    [WarningSeverity.INFO]:
      'Posibilidad de nieve ligera en algunas zonas de tu ruta.',
    [WarningSeverity.WARNING]:
      'Se prevé nieve en tu ruta. Lleva cadenas y conduce con precaución.',
    [WarningSeverity.DANGER]:
      'Nevadas intensas previstas. Considera retrasar tu viaje o usar una ruta alternativa.',
    [WarningSeverity.EXTREME]:
      '¡Alerta roja por nieve! Se recomienda encarecidamente no viajar.',
  },
  [WarningTypes.STORM]: {
    title: 'Alerta por Tormenta',
    [WarningSeverity.INFO]: 'Posibilidad de tormenta eléctrica aislada.',
    [WarningSeverity.WARNING]:
      'Tormentas eléctricas previstas en tu ruta. Evita zonas abiertas.',
    [WarningSeverity.DANGER]:
      'Tormentas severas en tu ruta. Busca refugio si es necesario.',
    [WarningSeverity.EXTREME]:
      '¡Alerta roja por tormenta! Evita viajar hasta que pase la tormenta.',
  },
  [WarningTypes.HEAVY_RAIN]: {
    title: 'Alerta por Lluvia Intensa',
    [WarningSeverity.INFO]: 'Lluvia ligera prevista en algunas zonas.',
    [WarningSeverity.WARNING]:
      'Lluvia moderada a intensa. Reduce la velocidad y aumenta la distancia de seguridad.',
    [WarningSeverity.DANGER]:
      'Lluvia muy intensa. Riesgo de aquaplaning. Conduce con extrema precaución.',
    [WarningSeverity.EXTREME]:
      '¡Alerta roja por lluvias! Posibles inundaciones. No cruces zonas inundadas.',
  },
  [WarningTypes.STRONG_WIND]: {
    title: 'Alerta por Viento Fuerte',
    [WarningSeverity.INFO]: 'Vientos moderados previstos.',
    [WarningSeverity.WARNING]:
      'Vientos fuertes. Precaución especial para vehículos altos y remolques.',
    [WarningSeverity.DANGER]:
      'Vientos muy fuertes. Riesgo para la conducción. Considera retrasar el viaje.',
    [WarningSeverity.EXTREME]:
      '¡Alerta roja por viento! Se desaconseja circular.',
  },
  [WarningTypes.FOG]: {
    title: 'Alerta por Niebla',
    [WarningSeverity.INFO]: 'Posible bruma o niebla ligera.',
    [WarningSeverity.WARNING]:
      'Niebla moderada. Enciende las luces antiniebla y reduce la velocidad.',
    [WarningSeverity.DANGER]:
      'Niebla densa. Visibilidad muy reducida. Extrema precaución.',
    [WarningSeverity.EXTREME]:
      'Niebla muy densa. Considera no viajar hasta que mejore la visibilidad.',
  },
  [WarningTypes.ICE]: {
    title: 'Alerta por Hielo',
    [WarningSeverity.INFO]:
      'Temperaturas bajas. Posible hielo en zonas de sombra.',
    [WarningSeverity.WARNING]: 'Riesgo de placas de hielo. Conduce suavemente.',
    [WarningSeverity.DANGER]:
      'Alto riesgo de hielo en la calzada. Usa neumáticos de invierno.',
    [WarningSeverity.EXTREME]: '¡Carreteras heladas! Se recomienda no viajar.',
  },
  [WarningTypes.EXTREME_HEAT]: {
    title: 'Alerta por Calor Extremo',
    [WarningSeverity.INFO]: 'Temperaturas altas previstas.',
    [WarningSeverity.WARNING]:
      'Calor intenso. Lleva agua y comprueba el sistema de refrigeración.',
    [WarningSeverity.DANGER]:
      'Ola de calor. Evita viajar en las horas centrales del día.',
    [WarningSeverity.EXTREME]:
      '¡Calor extremo! Riesgo para la salud. Viaja solo si es imprescindible.',
  },
  [WarningTypes.EXTREME_COLD]: {
    title: 'Alerta por Frío Extremo',
    [WarningSeverity.INFO]: 'Temperaturas bajas previstas.',
    [WarningSeverity.WARNING]:
      'Frío intenso. Comprueba anticongelante y batería del vehículo.',
    [WarningSeverity.DANGER]:
      'Frío muy intenso. Lleva ropa de abrigo y kit de emergencia.',
    [WarningSeverity.EXTREME]:
      '¡Frío extremo! Riesgo de hipotermia. Evita paradas prolongadas.',
  },
};

// Icons for each warning type (using react-icons names)
export const warningIcons = {
  [WarningTypes.SNOW]: 'BsSnow',
  [WarningTypes.STORM]: 'BsCloudLightningRain',
  [WarningTypes.HEAVY_RAIN]: 'BsCloudRainHeavy',
  [WarningTypes.STRONG_WIND]: 'BsWind',
  [WarningTypes.FOG]: 'BsCloudFog',
  [WarningTypes.ICE]: 'BsThermometerSnow',
  [WarningTypes.EXTREME_HEAT]: 'BsThermometerHigh',
  [WarningTypes.EXTREME_COLD]: 'BsThermometerLow',
};

// Colors for severity levels
export const severityColors = {
  [WarningSeverity.INFO]: {
    bg: 'blue.50',
    border: 'blue.400',
    text: 'blue.800',
    icon: 'blue.500',
  },
  [WarningSeverity.WARNING]: {
    bg: 'yellow.50',
    border: 'yellow.400',
    text: 'yellow.800',
    icon: 'yellow.500',
  },
  [WarningSeverity.DANGER]: {
    bg: 'orange.50',
    border: 'orange.400',
    text: 'orange.800',
    icon: 'orange.500',
  },
  [WarningSeverity.EXTREME]: {
    bg: 'red.50',
    border: 'red.400',
    text: 'red.800',
    icon: 'red.500',
  },
};
